import { useState, useEffect, useCallback } from 'react'

const VALUE = 'value'
const ERROR = 'error'
const REQUIRED_FIELD_ERROR = (propertyName, msgPrefix) => {
  let label = ''
  if (!propertyName) {
    label = 'This is required field'
  } else if (propertyName === 'Confirm account') {
    label = `Please confirm your account number`
  } else {
    label = `${msgPrefix}${
      (typeof propertyName === 'string' && propertyName.toLowerCase()) ||
      propertyName
    }`
  }
  return label
}

const CONFIRM_WITH_ERROR = () =>
  `Please ensure both the account numbers entered are the same.`
/**
 * Determines a value if it's an object
 *
 * @param {object} value
 */
function isObject(value) {
  return typeof value === 'object' && value !== null
}

/**
 *
 * @param {string} value
 * @param {boolean} isReq
 * @param {boolean} propName
 */
function isRequired(value, isReq, propName, msgPrefix) {
  if (!value && isReq) return REQUIRED_FIELD_ERROR(propName, msgPrefix)
  return ''
}

function getPropvalues(stateSchema, prop) {
  return Object.keys(stateSchema).reduce((accumulator, curr) => {
    accumulator[curr] = !prop ? false : stateSchema[curr][prop]
    return accumulator
  }, {})
}

/**
 * Custom hooks to validate your Form...
 *
 * @param {object} stateSchema model you stateSchema.
 * @param {object} stateValidatorSchema model your validation.
 * @param {function} submitFormCallback function to be execute during form submission.
 * @param {string} intl message for display empty error message.
 */
function useForm(
  submitFormCallback,
  msgPrefix = '',
  stateSchema = {},
  stateValidatorSchema = {}
) {
  const [state, setStateSchema] = useState(stateSchema)
  const [values, setValues] = useState(getPropvalues(state, VALUE))
  const [errors, setErrors] = useState(getPropvalues(state, ERROR))
  const [dirty, setDirty] = useState(getPropvalues(state))
  const [disable, setDisable] = useState(true)
  const [isDirty, setIsDirty] = useState(false)

  // Validate fields in forms
  const validateFormFields = useCallback(
    (name, value) => {
      const validator = stateValidatorSchema
      // Making sure that stateValidatorSchema name is same in
      // stateSchema
      if (!validator[name]) return

      const field = validator[name]
      const propName = field && field.propName

      let error = ''
      error = isRequired(value, field.required, propName, msgPrefix)

      if (isObject(field.validator) && error === '') {
        const fieldValidator = field.validator

        // Test the function callback if the value is meet the criteria
        const testFunc = fieldValidator.func
        if (!testFunc(value, values)) {
          error = fieldValidator.error
        }
      }

      // Test confirm property
      if (field.confirmWith && value) {
        const confirmPropertyName = field.confirmWith
        const accountPropertyName = field.confirmWith
        error =
          values[accountPropertyName] !== values[confirmPropertyName]
            ? CONFIRM_WITH_ERROR()
            : error
      }

      // eslint-disable-next-line consistent-return
      return error
    },
    [msgPrefix, stateValidatorSchema, values]
  )

  // Set Initial Error State
  // When hooks was first rendered...
  const setInitialErrorState = useCallback(
    () =>
      Object.keys(errors).map((name) =>
        setErrors((prevState) => ({
          ...prevState,
          [name]: validateFormFields(name, values[name])
        }))
      ),
    [errors, values, validateFormFields]
  )

  // Used to disable submit button if there's a value in errors
  // or the required field in state has no value.
  // Wrapped in useCallback to cached the function to avoid intensive memory leaked
  // in every re-render in component
  // eslint-disable-next-line
  const validateErrorState = useCallback(
    () => Object.values(errors).some((error) => error),
    [errors]
  )

  // Get a local copy of stateSchema
  useEffect(() => {
    setStateSchema(stateSchema)
    setDisable(true) // Disable button in initial render.
    setInitialErrorState()
  }, []) // eslint-disable-line

  // For every changed in our state this will be fired
  // To be able to disable the button
  useEffect(() => {
    if (isDirty) {
      setDisable(validateErrorState())
    }
  }, [errors, isDirty]) // eslint-disable-line

  // Event handler for handling changes in input.
  const handleOnChange = useCallback(
    (event) => {
      setIsDirty(true)
      const { name, value } = event.target ? event.target : event
      const error = validateFormFields(name, value)
      setValues((prevState) => ({ ...prevState, [name]: value }))
      setErrors((prevState) => ({ ...prevState, [name]: error }))
      setDirty((prevState) => ({ ...prevState, [name]: true }))
    },
    [validateFormFields]
  )

  const handleOnSubmit = useCallback(
    (event) => {
      event.preventDefault()
      // Making sure that there's no error in the state
      // before calling the submit callback function
      submitFormCallback(values, errors)
    },
    [submitFormCallback, values, errors]
  )

  const handleResetForm = useCallback(
    (resetStateSchema) => {
      setStateSchema(resetStateSchema)
      setValues(getPropvalues(resetStateSchema, VALUE))
      setErrors(getPropvalues(state, ERROR))
      setDirty(getPropvalues(state))
    },
    [state]
  )

  const validateForm = () => {
    let newErrors = {}
    Object.keys(errors).forEach((name) => {
      newErrors = {
        ...newErrors,
        [name]: validateFormFields(name, values[name])
      }
    })
    setErrors((prevState) => ({
      ...prevState,
      ...newErrors
    }))

    return newErrors
  }

  return {
    validateForm,
    handleOnChange,
    handleOnSubmit,
    values,
    errors,
    disable,
    setValues,
    setErrors,
    dirty,
    handleResetForm
  }
}
export default useForm
